// apps/app/src/features/auth/login.page.tsx

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LockKeyhole, LogIn, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api-client';
import { AuthField, AuthHeading, AuthLayout, IconInput, PasswordInput } from './auth-layout';
import { DevQuickLogin } from './dev-quick-login';

export function LoginPage() {
  const { t } = useTranslation('auth');
  const login = useAuth((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const signIn = async (withEmail: string, withPassword: string): Promise<void> => {
    setBusy(true);
    setError(null);

    try {
      await login(withEmail, withPassword);
      // Back to where they were heading before the redirect.
      navigate((location.state as { from?: string } | null)?.from ?? '/', { replace: true });
    } catch (err) {
      // The server does not distinguish "unknown email" from "wrong password",
      // and neither does this — that distinction is an enumeration oracle.
      setError(err instanceof ApiError ? err.message : t('login.error'));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    void signIn(email, password);
  };

  // Fills the form as well as signing in, so it is visible which account is
  // being used rather than landing in the dashboard as an unknown role.
  const quickLogin = (withEmail: string, withPassword: string): void => {
    setEmail(withEmail);
    setPassword(withPassword);
    void signIn(withEmail, withPassword);
  };

  return (
    <AuthLayout aside={{ text: t('login.noAccount'), action: t('login.createAccount'), to: '/register' }}>
      <AuthHeading icon={LogIn} title={t('login.title')} subtitle={t('login.subtitle')} />

      <form onSubmit={submit} className="space-y-5">
        <AuthField id="login-email" label={t('fields.email')}>
          <IconInput
            id="login-email"
            icon={Mail}
            type="email"
            required
            autoComplete="email"
            placeholder={t('placeholders.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </AuthField>

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between">
            <Label htmlFor="login-password">{t('fields.password')}</Label>
            <Link to="/reset-password" className="text-sm text-muted-foreground hover:text-foreground">
              {t('login.forgotPassword')}
            </Link>
          </div>
          <PasswordInput
            id="login-password"
            icon={LockKeyhole}
            required
            autoComplete="current-password"
            placeholder={t('placeholders.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        {/* Keeps its width while loading — a button that resizes reads as a
            layout bug at the exact moment somebody is waiting. */}
        <Button type="submit" loading={busy} className="h-11 w-full">
          {t('login.submit')}
        </Button>

        <DevQuickLogin onPick={quickLogin} disabled={busy} />
      </form>
    </AuthLayout>
  );
}
